import { call, takeEvery, put } from 'redux-saga/effects';
import { CloudActions } from '@portal/portal-core';

// `../api` is untyped JS; sendAction resolves as `any`.
import { sendAction } from '../api';
// `../shared` is untyped JS; transformUser resolves as `any`.
import { transformUser } from '../shared';
import {
  updateUserFieldSuccess,
  updateUserFieldError,
  setUserPhantomSuccess,
  setUserPhantomError,
  setUserEmailVerifiedSuccess,
  setUserEmailVerifiedError,
} from '../actions';
import {
  SUPER_ADMIN_UPDATE_USER_FIELD,
  SUPER_ADMIN_SET_USER_PHANTOM,
  SUPER_ADMIN_SET_USER_EMAIL_VERIFIED,
} from '../types';

// Minimal shape of the superadmin API config threaded through the untyped api/shared helpers.
interface SuperClientConfig {
  baseApiUrl: string;
  getAuthToken: () => string;
  timeout?: number;
  db?: {
    users?: {
      update: (id: string, changes: unknown) => unknown;
    };
  };
}

// Action dispatched to trigger the update-user-field saga.
interface UpdateUserFieldAction {
  type: string;
  userEmail: string;
  fieldKey: string;
  value: unknown;
}

// Action dispatched to trigger the set-user-phantom saga.
interface SetUserPhantomAction {
  type: string;
  userId: string;
  isPhantom: boolean;
}

// Action dispatched to trigger the set-user-email-verified saga.
interface SetUserEmailVerifiedAction {
  type: string;
  email: string;
  verified: boolean;
}

// Wrapped response returned by `sendAction` for a single-user admin mutation.
interface UpdateUserResponse {
  succeeded?: boolean;
  data?: { id: string; primaryEmail: string } & Record<string, unknown>;
}

export function* updateUserFieldSaga(
  config: SuperClientConfig,
  action: UpdateUserFieldAction,
): Generator<unknown, void, unknown> {
  try {
    const { userEmail, fieldKey, value } = action;
    const cloudAction = CloudActions.adminSetUserKeyValue(userEmail, fieldKey, value);
    const res = (yield call(sendAction, config, cloudAction)) as UpdateUserResponse;
    if (res.succeeded) {
      if (res.data) {
        const transformed = transformUser(res.data);
        if (config.db?.users) {
          yield config.db.users.update(res.data.id, transformed);
        }
        yield put(updateUserFieldSuccess(transformed));
      } else {
        // API confirmed success but returned no user payload — the optimistic
        // update in the component already reflects the change.
        yield put(updateUserFieldSuccess(null));
      }
    } else {
      yield put(updateUserFieldError('Failed to update user field.'));
    }
  } catch (error) {
    console.error('updateUserField failed -- ', error);
    yield put(updateUserFieldError('An unexpected error occurred.'));
  }
}

export function* setUserPhantomSaga(
  config: SuperClientConfig,
  action: SetUserPhantomAction,
): Generator<unknown, void, unknown> {
  try {
    const { userId, isPhantom } = action;
    const cloudAction = CloudActions.adminSetUserPhantom(userId, isPhantom);
    const res = (yield call(sendAction, config, cloudAction)) as UpdateUserResponse;
    if (res.succeeded) {
      const transformed = transformUser(res.data!);
      if (config.db?.users) {
        yield config.db.users.update(res.data!.id, transformed);
      }
      yield put(setUserPhantomSuccess(transformed));
    } else {
      yield put(setUserPhantomError('Failed to update phantom status.'));
    }
  } catch (error) {
    console.error('setUserPhantom failed -- ', error);
    yield put(setUserPhantomError('An unexpected error occurred.'));
  }
}

export function* setUserEmailVerifiedSaga(
  config: SuperClientConfig,
  action: SetUserEmailVerifiedAction,
): Generator<unknown, void, unknown> {
  try {
    const { email, verified } = action;
    const cloudAction = CloudActions.adminSetEmailVerificationState(email, verified);
    const res = (yield call(sendAction, config, cloudAction)) as UpdateUserResponse;
    if (res.succeeded) {
      if (res.data) {
        const transformed = transformUser(res.data);
        if (config.db?.users) {
          yield config.db.users.update(res.data.id, transformed);
        }
        yield put(setUserEmailVerifiedSuccess(transformed));
      } else {
        yield put(setUserEmailVerifiedSuccess(null));
      }
    } else {
      yield put(setUserEmailVerifiedError('Failed to update email verification.'));
    }
  } catch (error) {
    console.error('setUserEmailVerified failed -- ', error);
    yield put(setUserEmailVerifiedError('An unexpected error occurred.'));
  }
}

// TODO: password reset is deferred until a restricted admin role is available

/* istanbul ignore next reason: no saga watch tests */
export function* watchUpdateUserField(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeEvery(SUPER_ADMIN_UPDATE_USER_FIELD, updateUserFieldSaga, config);
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchSetUserPhantom(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeEvery(SUPER_ADMIN_SET_USER_PHANTOM, setUserPhantomSaga, config);
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchSetUserEmailVerified(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeEvery(SUPER_ADMIN_SET_USER_EMAIL_VERIFIED, setUserEmailVerifiedSaga, config);
}
